import { defineStore } from 'pinia'
import { reactive } from 'vue'
import type ChatRoom from '@/types/ChatRoom'
import type ChatMessage from '@/types/ChatMessage'
import type ChatParticipant from '@/types/ChatParticipant'
import type ChatRoomReadStatus from '@/types/ChatRoomReadStatus'
import Backend from '@/models/backend'
import { playNewChatMessage } from '@/models/sounds'
import { useGameStore } from '@/stores/gameStore'
import router from '@/router'
import { logger } from '@/models/logger'

/**
 * Constants
 */
const DRAFT_MESSAGES_STORAGE_KEY = 'ferion_chat_draft_messages_v1'
const ACTIVE_ROOM_STORAGE_KEY = 'ferion_chat_active_room_v1'

/**
 * Load saved draft messages from localStorage
 */
const loadSavedDraftMessages = (): Map<number, string> => {
  try {
    const savedDrafts = localStorage.getItem(DRAFT_MESSAGES_STORAGE_KEY)
    if (savedDrafts) {
      const draftsArray = JSON.parse(savedDrafts) as [number, string][]
      return new Map(draftsArray)
    }
  } catch (error) {
    logger.error('Error loading draft messages from localStorage:', error)
  }
  return new Map<number, string>()
}

/**
 * Load saved active room ID from localStorage
 */
const loadSavedActiveRoomId = (): number => {
  try {
    const savedActiveRoomId = localStorage.getItem(ACTIVE_ROOM_STORAGE_KEY)
    if (savedActiveRoomId) {
      return parseInt(savedActiveRoomId, 10)
    }
  } catch (error) {
    logger.error('Error loading active room ID from localStorage:', error)
  }
  return 0
}

export const useChatStore = defineStore('chatStore', {
  state: () => ({
    rooms: new Map<number, ChatRoom>(),
    roomMessages: reactive(new Map<number, Map<number, ChatMessage>>()),
    roomParticipants: reactive(new Map<number, Map<number, ChatParticipant>>()), // List of participants in the room
    roomReadStatus: reactive(new Map<number, ChatRoomReadStatus>()), // what is the last message read by the user? and last message received by the user?
    roomData: reactive(
      new Map<
        number,
        {
          messagesInitialized: boolean
          participantsInitialized: boolean
          messagesLoaded: boolean
        }
      >()
    ),
    activeRoomId: loadSavedActiveRoomId(),
    visibleRoomId: 0 as number,
    draftMessages: reactive(loadSavedDraftMessages()), // Load draft messages from localStorage
    mutedPlayerIds: [] as number[] // Array of player IDs that are muted by the current player
  }),

  getters: {},

  actions: {
    // ===== Room Management =====
    getRoomByRoomKey(roomKey: string): ChatRoom | undefined {
      for (const room of this.rooms.values()) {
        if (room.roomKey === roomKey && room.deleted === 0) {
          return room
        }
      }
      return undefined
    },

    getRoomById(roomId: number): ChatRoom | undefined {
      // If the room is deleted, return undefined
      if (this.rooms.get(roomId)?.deleted === 1) {
        return undefined
      }
      return this.rooms.get(roomId)
    },

    setRoom(room: ChatRoom) {
      if (!this.rooms.has(room.id)) {
        const roomData = {
          messagesInitialized: false,
          participantsInitialized: false,
          messagesLoaded: false
        }
        this.roomData.set(room.id, roomData)
      } else {
        // Check if participantsMD5 has changed
        const existingRoom = this.rooms.get(room.id)
        if (existingRoom && existingRoom.participantsMD5 !== room.participantsMD5) {
          // Clear participants for this room and reset initialization status
          // Participants will need to be reloaded by the component when needed
          this.clearRoomParticipants(room.id)
        }
      }

      logger.debug('setRoom', room.id, room.participantsMD5)

      // Always set the last message id
      if (this.getRoomLastMessageReceivedId(room.id) < room.lastMessageId) {
        this.setRoomLastMessageReceivedId(room.id, room.lastMessageId)
      }
      // Always set the last message read id
      if (this.getRoomLastMessageReadId(room.id) < room.lastMessageReadId) {
        this.setRoomLastMessageReadId(room.id, room.lastMessageReadId)
      }

      // If the room is deleted, set the active room id to 0
      if (room.deleted === 1 && this.activeRoomId === room.id) {
        this.activeRoomId = 0
      }

      this.rooms.set(room.id, room)
    },

    setActiveRoomId(activeRoomId: number) {
      this.activeRoomId = activeRoomId
      this.setVisibleRoomId(activeRoomId)
      this.saveActiveRoomIdToLocalStorage()
    },

    setVisibleRoomId(visibleRoomId: number) {
      this.visibleRoomId = visibleRoomId
    },

    // ===== Message Management =====
    setMessage(message: ChatMessage) {
      // Store the message
      const roomId = message.roomId
      if (!this.roomMessages.has(roomId)) {
        this.roomMessages.set(roomId, reactive(new Map<number, ChatMessage>()))
      }
      this.roomMessages.get(roomId)!.set(message.id, message)

      logger.debug('setMessage', message.id, message.roomId)
      if (message.roomId !== this.activeRoomId) {
        playNewChatMessage(0.7)
      }

      // set the last message received id (if its id is higher than the last message received id)
      if (message.id > this.getRoomLastMessageReceivedId(roomId)) {
        this.setRoomLastMessageReceivedId(roomId, message.id)
      }

      // Only mark message as read if:
      // 1. Player is currently on the Communication page
      // 2. The message is for the currently active room
      const isOnCommunicationPage = router.currentRoute.value.name === 'Communication'
      if (isOnCommunicationPage && message.roomId === this.activeRoomId) {
        // If the message we recieved is newer than the last message read, set it as read
        if (message.id > this.getRoomLastMessageReadId(roomId)) {
          this.setMessageAsRead(roomId, message.id)
        }
      }
    },

    // ===== Participant Management =====
    setParticipant(participant: ChatParticipant) {
      const roomId = participant.chatRoomId
      if (!this.roomParticipants.has(roomId)) {
        this.roomParticipants.set(roomId, reactive(new Map<number, ChatParticipant>()))
      }
      this.roomParticipants.get(roomId)!.set(participant.id, participant)
    },

    clearRoomParticipants(roomId: number) {
      // Clear the participants map for the specified room
      if (this.roomParticipants.has(roomId)) {
        this.roomParticipants.delete(roomId)
      }
      // Reset the participants initialization status
      if (this.roomData.has(roomId)) {
        this.roomData.get(roomId)!.participantsInitialized = false
      }
    },

    loadRoomParticipants(roomId: number) {
      if (roomId > 0 && !this.getParticipantsInitialized(roomId)) {
        const backend = new Backend()
        backend
          .postRoomParticipants(roomId)
          .then(() => this.setParticipantsInitialized(roomId))
          .catch((err) => logger.error('Error loading room participants:', err))
      }
    },

    // ===== Room Status Management =====
    setRoomAsRead(roomId: number) {
      const messages = this.roomMessages.get(roomId)

      // If messages aren't loaded yet, use the room's lastMessageId
      if (!messages || messages.size === 0) {
        const room = this.rooms.get(roomId)
        if (room) {
          this.setRoomLastMessageReadId(roomId, room.lastMessageId)
          const backend = new Backend()
          backend.setChatRoomAsRead(roomId, room.lastMessageId)
        }
        return
      }

      // Messages are loaded, find the highest message ID
      const highestMessageId = Math.max(
        ...Array.from(messages.values()).map((message) => message.id)
      )
      this.setRoomLastMessageReadId(roomId, highestMessageId)
      const backend = new Backend()
      backend.setChatRoomAsRead(roomId, highestMessageId)
    },

    setMessageAsRead(roomId: number, messageId: number) {
      this.setRoomLastMessageReadId(roomId, messageId)
      const backend = new Backend()
      backend.setChatRoomAsRead(roomId, messageId)
    },

    // ===== Room Data Management =====
    getMessagesInitialized(roomId: number) {
      if (this.roomData.has(roomId)) {
        return this.roomData.get(roomId)!.messagesInitialized
      }
      return false
    },

    setMessagesInitialized(roomId: number) {
      if (this.roomData.has(roomId)) {
        this.roomData.get(roomId)!.messagesInitialized = true
      }
    },

    getParticipantsInitialized(roomId: number) {
      if (this.roomData.has(roomId)) {
        return this.roomData.get(roomId)!.participantsInitialized
      }
      return false
    },

    setParticipantsInitialized(roomId: number) {
      if (this.roomData.has(roomId)) {
        this.roomData.get(roomId)!.participantsInitialized = true
      }
    },

    getMessagesLoaded(roomId: number) {
      if (this.roomData.has(roomId)) {
        return this.roomData.get(roomId)!.messagesLoaded
      }
      return false
    },

    setMessagesLoaded(roomId: number) {
      if (this.roomData.has(roomId)) {
        this.roomData.get(roomId)!.messagesLoaded = true
      }
    },

    // ===== Message Read Status Management =====
    setRoomLastMessageReceivedId(roomId: number, lastMessageId: number) {
      let status: ChatRoomReadStatus
      if (this.roomReadStatus.has(roomId)) {
        status = this.roomReadStatus.get(roomId)!
      } else {
        status = {
          lastMessageReadId: 0,
          lastMessageReceivedId: lastMessageId,
          unreadStatus: false
        }
      }
      status.lastMessageReceivedId = lastMessageId
      status.unreadStatus = lastMessageId > status.lastMessageReadId
      this.roomReadStatus.set(roomId, status)
    },

    setRoomLastMessageReadId(roomId: number, lastMessageId: number) {
      let status: ChatRoomReadStatus
      if (this.roomReadStatus.has(roomId)) {
        status = this.roomReadStatus.get(roomId)!
      } else {
        status = {
          lastMessageReadId: lastMessageId,
          lastMessageReceivedId: 0,
          unreadStatus: false
        }
      }
      status.lastMessageReadId = lastMessageId
      status.unreadStatus = status.lastMessageReceivedId > lastMessageId
      this.roomReadStatus.set(roomId, status)
    },

    getRoomLastMessageReceivedId(roomId: number) {
      if (this.roomReadStatus.has(roomId)) {
        return this.roomReadStatus.get(roomId)!.lastMessageReceivedId
      }
      return 0
    },

    getRoomLastMessageReadId(roomId: number) {
      if (this.roomReadStatus.has(roomId)) {
        return this.roomReadStatus.get(roomId)!.lastMessageReadId
      }
      return 0
    },

    getCountOfUnreadMessages() {
      let count = 0
      for (const [roomId, status] of this.roomReadStatus.entries()) {
        if (status.unreadStatus) {
          const room = this.rooms.get(roomId)
          if (room && room.deleted === 0) {
            count++
          }
        }
      }
      return count
    },

    // ===== Draft Message Management =====
    getDraftMessage(roomId: number): string {
      return this.draftMessages.get(roomId) || ''
    },

    setDraftMessage(roomId: number, message: string) {
      this.draftMessages.set(roomId, message)
      this.saveDraftMessagesToLocalStorage()
    },

    clearDraftMessage(roomId: number) {
      this.draftMessages.delete(roomId)
      this.saveDraftMessagesToLocalStorage()
    },

    saveDraftMessagesToLocalStorage() {
      try {
        const draftsArray = Array.from(this.draftMessages.entries())
        localStorage.setItem(DRAFT_MESSAGES_STORAGE_KEY, JSON.stringify(draftsArray))
      } catch (error) {
        logger.error('Error saving draft messages to localStorage:', error)
      }
    },

    saveActiveRoomIdToLocalStorage() {
      try {
        localStorage.setItem(ACTIVE_ROOM_STORAGE_KEY, this.activeRoomId.toString())
      } catch (error) {
        logger.error('Error saving active room ID to localStorage:', error)
      }
    },

    // Open existing chat or create new one with a player
    async openOrCreateChatWithPlayer(playerId: number): Promise<number> {
      const gameStore = useGameStore()

      // Generate the room key for this private chat
      const roomKey =
        'private_' +
        Math.min(playerId, gameStore.player!.id) +
        '_' +
        Math.max(playerId, gameStore.player!.id)

      // Check if room already exists
      const existingRoom = this.getRoomByRoomKey(roomKey)

      if (existingRoom) {
        // Room exists, set it as active and return its id
        this.setActiveRoomId(existingRoom.id)
        return existingRoom.id
      } else {
        // Room doesn't exist, create it
        const backend = new Backend()
        const response = await backend.createPrivateChatRoom(playerId)

        this.setRoom(response.data.chatRoom)
        this.setActiveRoomId(response.data.chatRoom.id)
        return response.data.chatRoom.id
      }
    },

    // ===== Muted Players Management =====
    setMutedPlayerIds(mutedPlayerIds: number[]) {
      this.mutedPlayerIds = mutedPlayerIds
      logger.debug('Updated muted player IDs:', mutedPlayerIds)
    },

    isPlayerMuted(playerId: number): boolean {
      return this.mutedPlayerIds.includes(playerId)
    },

    async mutePlayer(playerId: number): Promise<void> {
      try {
        const backend = new Backend()
        await backend.mutePlayer(playerId)
        // The websocket update will handle updating mutedPlayerIds
        logger.debug('Player muted:', playerId)
      } catch (error) {
        logger.error('Error muting player:', error)
        throw error
      }
    },

    async unmutePlayer(playerId: number): Promise<void> {
      try {
        const backend = new Backend()
        await backend.unmutePlayer(playerId)
        // The websocket update will handle updating mutedPlayerIds
        logger.debug('Player unmuted:', playerId)
      } catch (error) {
        logger.error('Error unmuting player:', error)
        throw error
      }
    }
  }
})
